import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'

import clsx from 'clsx'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ResponsiveImage from '../ResponsiveImage'
import TextButton from 'LuxKit/Button/TextButton'
import { dateDifference } from 'lib/datetime/dateUtils'
import OfferUrgencyCountdownLabel from '../Labels/OfferUrgencyCountdownLabel'
import ClickableLink from '../Clickable/ClickableLink'
import Sheet from 'components/utils/Sheet'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'

const MainContainer = styled.div`
  overflow: hidden;

  &.is-carousel {
    overflow: visible;
    width: ${rem(300)};

    ${mediaQueryUp.tablet} {
      width: ${rem(560)};
    }
  }
`

const DealsContainer = styled.div`
  display: flex;
  height: ${rem(125)};

  ${mediaQueryUp.tablet} {
    height: ${rem(170)};
  }
`

const ColourContainer = styled.div`
  padding: ${rem(4)} ${rem(16)} ${rem(16)} ${rem(16)};
  width: 60%;
  height: 100%;

  &.is-carousel {
    width: 100%;

    ${mediaQueryUp.tablet} {
      width: 55%;
    }
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    width: 55%;
    height: 100%;
  }
`

const LogoContainer = styled.div`
  width: ${rem(64)};
  margin-bottom: ${rem(8)};

  ${mediaQueryUp.tablet} {
    width: ${rem(84)};
    margin-bottom: 0;
  }
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 40%;
  height: 100%;

  &.is-carousel {
    width: ${rem(260)};
  }

  ${mediaQueryUp.tablet} {
    width: 45%;
  }
`

const ButtonContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    position: absolute;
    z-index: 999;
    right: ${rem(20)};
    bottom: ${rem(20)};
  }
`

interface Props {
  backgroundColour: string
  logoImageId: string
  shipImageId: string
  heading: string
  subHeading: string
  endDate: string
  slug: string
  variant: 'carousel' | 'grid'
}

function DealsTileItem(props: Props) {
  const { backgroundColour, logoImageId, shipImageId, heading, subHeading, endDate, slug, variant } = props
  const urlDealPage = `/cruises/deal/${slug}`
  const [isHovered, setIsHovered] = useState(false)

  const shouldShowCountdown = useMemo(() => {
    const daysToShowCountdown = 7
    return dateDifference(new Date(endDate), new Date()).days < daysToShowCountdown
  }, [endDate])

  const isCarousel = variant === 'carousel'

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5, // At least 50% of the element must be visible
  })

  useEffect(() => {
    if (inView) {
      trackEvent(promotionViewEvent({
        promotionContext: {
          promotionName: heading,
          promotionType: 'banner',
        },
      }))
    }
  }, [inView, heading])

  const onDealClick = useCallback(() => {
    trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: heading,
        promotionType: 'banner',
      },
    }))
  }, [heading])

  return <ClickableLink to={urlDealPage} onClick={onDealClick} preventSwipeClick>
    <MainContainer className={clsx({ 'is-carousel': isCarousel })} ref={inViewRef}>
      <Group direction="vertical" gap={8}>
        <DealsContainer onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <ColourContainer className={clsx({ 'is-carousel': isCarousel })} style={{ backgroundColor: backgroundColour }}>
            <Group direction="vertical" verticalAlign="space-between" fullHeight>
              <LogoContainer>
                <ResponsiveImage
                  id={logoImageId}
                  src={logoImageId}
                  quality="eco"
                  role="presentation"
                  alt={heading}
                />
              </LogoContainer>
              <CSSBreakpoint max="mobile">
                <BodyTextBlock variant="small" colour="dark-contrast" weight="bold">
                  {heading}
                </BodyTextBlock>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <BodyTextBlock variant="large" colour="dark-contrast">
                  {heading}
                </BodyTextBlock>
              </CSSBreakpoint>
            </Group>
          </ColourContainer>
          <ImageContainer className={clsx({ 'is-carousel': isCarousel })}>
            {isHovered && <ButtonContainer>
              <TextButton kind="primary" variant="ghost" size="small">
                <FormattedMessage
                  id="gvd4o3jerViUmSg7riw4"
                  defaultMessage="View all offers"
                  description="Button text to view all cruise deal offers in the cruise section"
                />
              </TextButton>
            </ButtonContainer>}
            <ResponsiveImage
              fit="center"
              id={shipImageId}
              src={shipImageId}
              width={42}
              quality="eco"
              role="presentation"
              alt={heading}
            />
          </ImageContainer>
        </DealsContainer>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {subHeading}
          </BodyText>
          {!shouldShowCountdown && <BodyText variant="small">
            <FormattedMessage
              id="un3z1ZbLMJ26p7AR6tsT"
              defaultMessage="Offer ends on {expiryDate}"
              description="Text showing when a deal offer expires in the date selection or calendar picker"
              values={{ expiryDate: <FormatDateTime value={endDate} format="dmy-casual" /> }}
            />
          </BodyText>}
        </Group>
      </Group>
      {shouldShowCountdown && <Sheet padding="4 0">
        <OfferUrgencyCountdownLabel
          endDate={endDate}
          tagPlace="deals-tile-item"
        />
      </Sheet>}
    </MainContainer>
  </ClickableLink>
}

export default DealsTileItem
